import * as auth from "./auth";
import * as externalAuthorizations from "./externalAuthorizations.ts";
import * as config from "./config";
import * as users from "./users";
import * as roles from "./roles.ts";
import * as userLoginMethod from "./userLoginMethod.ts";
import * as files from "./files";
import * as activities from "./activities";
import * as supportEmails from "./supportEmails";
import * as backgroundTasks from "./backgroundTasks";
import * as notifications from "./notifications/inAppNotifications.ts";
import * as notificationChannels from "./notifications/notificationChannels.ts";
import * as notificationPreferences from "./notifications/notificationPreferences.ts";
import * as watcherPreferences from "./notifications/watcherPreferences.ts";
import * as accessTokens from "./accessTokens.ts";
import * as teams from "./teams.ts";
import * as systemAgents from "./systemAgents";
import * as owners from "./owners.ts";
import * as reportedErrors from "./reportedErrors.ts";
import * as reminderSchedules from "./reminderSchedules.ts";
import * as scim from "./scim";
import * as verifiedDomains from "./verifiedDomains";
import * as loginMethod from "./loginMethod";
import * as reviewable from "./reviewable";
import * as agentSessions from "./agentSessions";
import vendorToolkit from "./vendorToolkit";
import tprm from "./tprm";
import {ErrorResponse} from "../Types";

export class HTTPError extends Error {
  response: Response;
  json?: ErrorResponse;

  constructor(response: Response, json?: ErrorResponse) {
    super(`HTTP Error: ${response.status} ${response.statusText}`);
    this.response = response;
    this.json = json;
  }

  static async fromResponse(response: Response) {
    let json;
    if (!response.bodyUsed) {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        json = await response.json();
      }
    }
    return new HTTPError(response, json);
  }

  // Don't report 403 or 404 errors
  get _isNominal() {
    return this.response.status == 403 || this.response.status == 404;
  }

  display() {
    if (this.json) {
      return shortErrorMessage(this.json);
    } else {
      return this.message;
    }
  }
}

type FieldError = {
  message?: string;
};

// Format our error responses as a 1-line human readable message.
// This might hide issues with other fields
function shortErrorMessage(obj: ErrorResponse) {
  if (obj.type_ === "validation_error") {
    for (const [_, errors] of Object.entries(obj.extra as object)) {
      return errors.map((e: FieldError) => e?.message || "").join("\n");
    }
  }

  return obj.error;
}

const api = {
  users,
  roles,
  vendorToolkit,
  tprm,
  auth,
  externalAuthorizations,
  config,
  userLoginMethod,
  loginMethod,
  files,
  activities,
  supportEmails,
  backgroundTasks,
  notifications,
  notificationChannels,
  notificationPreferences,
  accessTokens,
  teams,
  systemAgents,
  owners,
  reportedErrors,
  reminderSchedules,
  scim,
  verifiedDomains,
  watcherPreferences,
  reviewable,
  agentSessions,
};

export default api;
